import React, { useEffect, useState, useRef } from "react";
import ELayout from "./ELayout";
import AxiosInstance from "../AxiosInstance";
import { Card, Row, Col, Button, Container } from "react-bootstrap";
import { FaProjectDiagram, FaClock, FaUserClock, FaCalendarAlt } from "react-icons/fa";
import ToastNotification from "../ToastNotification";
import { employeeDashboardConfig } from "../menuconfig"; 

const StatsCard = ({ title, children, bgColor, icon: Icon }) => (
  <Col md={4} className="mb-4 d-flex">
    <Card className="p-4 shadow-lg rounded-4 text-white stats-card w-100" style={{ backgroundColor: bgColor }}>
      <Card.Body className="text-center d-flex flex-column justify-content-center align-items-center">
        <Icon size={40} className="mb-2" />
        <Card.Title className="fw-bold fs-5 mt-2">{title}</Card.Title>
        <div className="fs-4 fw-semibold">{children}</div>
      </Card.Body>
    </Card>
  </Col>
);

const EHome = () => {
  const [fullName, setFullName] = useState("");
  const [projects, setProjects] = useState(0);
  const [hoursWorked, setHoursWorked] = useState({ week: 0, lastWeek: 0, month: 0 });
  const [leaveBalance, setLeaveBalance] = useState(0);
  const [holidays, setHolidays] = useState([]);
  const toastRef = useRef(null);
  const userId = localStorage.getItem("employeeId");
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().toLocaleString("default", { month: "long" });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!userId) throw new Error("User not logged in or userId not found.");

        const [userRes, projectsRes, hoursRes, leaveRes, holidaysRes] = await Promise.all([
          AxiosInstance.get(`/common/user/${userId}`),
          AxiosInstance.get(`/employee/fetchProjectsForEmployee/${userId}`),
          AxiosInstance.get(`/employee/work-hours/${userId}`),
          AxiosInstance.get(`/employee/getLeaveBalance/${userId}`),
          AxiosInstance.get(`/common/holidays/${currentYear}`)
        ]);

        setFullName(
          userRes.data.full_name
            .split(" ")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
        );
        setProjects(projectsRes.data.projectCount);
        setHoursWorked({
          week: hoursRes.data.week_hours,
          lastWeek: hoursRes.data.last_week_hours,
          month: hoursRes.data.month_hours
        });
        setLeaveBalance(
          leaveRes.data.filter(leave => leave.leave_name !== "LOP")
            .reduce((sum, leave) => sum + parseInt(leave.remaining_leave, 10), 0)
        );

        const today = new Date();
        const upcomingHolidays = holidaysRes.data
          .filter(holiday => new Date(holiday.date) >= today)
          .slice(0, 1);
        setHolidays(upcomingHolidays);
      } catch (error) {
        toastRef.current?.showToast("Error fetching data", "danger");
      }
    };

    fetchData();
  }, [userId, currentYear]);

  return (
    <ELayout heading="Dashboard">
      <Container>
        <h3 className="text-center fw-bold my-4" style={{ color: "#6c757d" }}>
          Hey {fullName}, welcome back! <span role="img" aria-label="rocket">🚀</span>
        </h3>

        <Row className="justify-content-center">
          {employeeDashboardConfig.showProjects && (
            <StatsCard title="Projects Assigned" icon={FaProjectDiagram} bgColor="#3917FE">
              {projects}
            </StatsCard>
          )}

          {employeeDashboardConfig.showHoursWorked && (
            <StatsCard title="Total Hours Worked" icon={FaClock} bgColor="#FD9323">
              <div className="text-center">
                <p className="mb-1"><strong>This Week:</strong> {hoursWorked.week} hrs</p>
                <p className="mb-1"><strong>Last Week:</strong> {hoursWorked.lastWeek} hrs</p>
                <p className="mb-0"><strong>{currentMonth}:</strong> {hoursWorked.month} hrs</p>
              </div>
            </StatsCard>
          )}

          {employeeDashboardConfig.showLeaveBalance && (
            <StatsCard title="Leave Balance" icon={FaUserClock} bgColor="#AF4582">
              {leaveBalance}
            </StatsCard>
          )}
        </Row>

        {/* Upcoming Holidays Section */}
        {employeeDashboardConfig.showUpcomingHolidays && (
          <div className="mt-4 text-center">
            <h4 className="fw-bold" style={{ color: "#6c757d" }}>Upcoming Holidays</h4>
            {holidays.length > 0 ? (
              <ul className="list-unstyled mt-3">
                {holidays.map((holiday, index) => (
                  <li key={index} className="fs-5">
                    <FaCalendarAlt className="me-2" style={{ color: "#3917FE" }} />
                    <strong>{holiday.description} - {new Date(holiday.date).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</strong>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted">No upcoming holidays!</p>
            )}
          </div>
        )}

        <Row className="mt-4">
          <Col className="d-flex flex-wrap justify-content-center gap-3">
            {employeeDashboardConfig.showTimesheetButton && (
              <Button 
                href="/weeksTimesheet" 
                className="fw-bold px-4 py-2 shadow-sm" 
                style={{ backgroundColor: "#FD9323", borderColor: "#B8B5FB" }}
              >
                Go to Timesheet
              </Button>
            )}

            {employeeDashboardConfig.showApplyLeaveButton && (
              <Button 
                href="/leave-request" 
                className="fw-bold px-4 py-2 shadow-sm" 
                style={{ backgroundColor: "#AF4582", borderColor: "#AF4582" }}
              >
                Apply Leave
              </Button>
            )}
          </Col>
        </Row>

        <ToastNotification ref={toastRef} />
      </Container>
    </ELayout>
  );
};

export default EHome;
